import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Plus, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    type TOTPEnrollment,
    useStartTOTPEnroll,
    useVerifyTOTPEnroll,
} from "~/lib/mfa";

import { CopyButton } from "./CopyButton";

const modalStyle: React.CSSProperties = { padding: "var(--space-6)" };

export function EnrollTOTPDialog(): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [enrollment, setEnrollment] = useState<TOTPEnrollment | null>(null);
    const [code, setCode] = useState("");
    const [label, setLabel] = useState("Authenticator app");
    const [error, setError] = useState<string | null>(null);

    const start = useStartTOTPEnroll();
    const verify = useVerifyTOTPEnroll();

    // Trigger the start mutation exactly once per open. Using a ref instead
    // of including `start` in the dep array avoids an infinite loop — the
    // mutation object's identity changes on every re-render.
    const triggered = useRef(false);
    useEffect(() => {
        if (!open) {
            triggered.current = false;
            return;
        }
        if (triggered.current) return;
        triggered.current = true;
        start.mutate(undefined, {
            onSuccess: (e) => setEnrollment(e),
            onError: (err) =>
                setError(err instanceof ApiError ? err.message : "Failed to start enrollment"),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function reset(): void {
        setEnrollment(null);
        setCode("");
        setLabel("Authenticator app");
        setError(null);
        start.reset();
        verify.reset();
    }

    async function onVerify(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        if (!enrollment) return;
        const trimmed = code.replace(/\s+/g, "");
        if (!/^\d{6}$/.test(trimmed)) {
            setError("Enter the 6-digit code from your app.");
            return;
        }
        try {
            await verify.mutateAsync({ secret: enrollment.secret, code: trimmed, label });
            toast.success("TOTP enrolled. Next sign-in will require it.");
            setOpen(false);
            reset();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to verify code");
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Dialog.Trigger asChild>
                <button type="button" className="btn btn--primary press-crush">
                    <Plus size={14} />
                    <span>Add authenticator app</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                    <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                        <Dialog.Title className="type-h2">Add authenticator app</Dialog.Title>
                        <Dialog.Close asChild>
                            <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                <X size={14} />
                            </button>
                        </Dialog.Close>
                    </div>
                    <Dialog.Description className="text-text-muted text-13" style={{ marginTop: "var(--space-2)" }}>
                        Scan the QR with 1Password, Authy, Google Authenticator, or any TOTP app, then enter
                        the 6-digit code it shows to confirm.
                    </Dialog.Description>

                    {!enrollment && (
                        <div className="empty-state" style={{ padding: "var(--space-8)" }}>
                            <Loader2 size={20} className="empty-state__icon animate-spin" aria-hidden />
                            <div className="empty-state__body">Generating secret…</div>
                        </div>
                    )}

                    {enrollment && (
                        <form onSubmit={onVerify} className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <div
                                className="surface"
                                style={{
                                    display: "grid",
                                    placeItems: "center",
                                    padding: "var(--space-4)",
                                    background: "#FFFFFF",
                                    borderRadius: "var(--radius-md)",
                                }}
                            >
                                <QRCodeSVG value={enrollment.otpauthUrl} size={180} includeMargin={false} />
                            </div>

                            <details>
                                <summary className="text-12 text-text-muted" style={{ cursor: "pointer" }}>
                                    Can't scan? Show secret manually.
                                </summary>
                                <div
                                    className="surface"
                                    style={{
                                        marginTop: "var(--space-2)",
                                        padding: "var(--space-3)",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "var(--space-3)",
                                        fontFamily: "var(--font-family-mono)",
                                        fontSize: "var(--font-size-12)",
                                    }}
                                >
                                    <span style={{ flex: 1, wordBreak: "break-all" }}>{enrollment.secret}</span>
                                    <CopyButton value={enrollment.secret} />
                                </div>
                            </details>

                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Label</span>
                                <input
                                    className="input"
                                    value={label}
                                    onChange={(e) => setLabel(e.target.value)}
                                    placeholder="e.g. 1Password — work"
                                />
                            </label>

                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Verification code</span>
                                <input
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    pattern="\d{6}"
                                    maxLength={6}
                                    className="input"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                                    placeholder="123 456"
                                    style={{
                                        textAlign: "center",
                                        fontFamily: "var(--font-family-mono)",
                                        fontSize: "var(--font-size-22)",
                                        letterSpacing: "0.4em",
                                    }}
                                    autoFocus
                                />
                            </label>

                            {error && (
                                <div className="chip chip--danger shake" style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}>
                                    {error}
                                </div>
                            )}

                            <button type="submit" className="btn btn--primary press-crush" disabled={verify.isPending}>
                                {verify.isPending ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                                <span>{verify.isPending ? "Verifying…" : "Verify and enable"}</span>
                            </button>
                        </form>
                    )}
                </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
